import React, { useState, useEffect } from "react";
import {
  Dropdown,
  Collapse,
  Checkbox,
  DatePicker,
  ConfigProvider,
  Button,
} from "antd";
import { ChevronRight, FilterIcon } from "assets/icons";
import useThemeStore from "(store)/themeStore";
import moment from "moment";

const { Panel } = Collapse;

const MultiCategoryFilter = ({ categories, onFilterChange }) => {
  const [open, setOpen] = useState(false);
  const theme = useThemeStore((state) => state.theme);
  const [filters, setFilters] = useState({});

  // Initialize filters based on categories
  useEffect(() => {
    const initialFilters = categories.reduce((acc, category) => {
      if (category.value === "dateRange") {
        acc[category.value] = { fromDate: null, toDate: null };
      } else {
        // If "All" is visible, select all sibling options
        const allOption = category.options.find((opt) => opt.value === "all");
        if (allOption && allOption.visible) {
          acc[category.value] = category.options
            .filter((option) => option.value !== "all")
            .map((option) => option.value);
        } else {
          acc[category.value] = [];
        }
      }
      return acc;
    }, {});
    setFilters(initialFilters);
    onFilterChange(initialFilters); // Notify parent of initial filters
  }, [categories]);

  const anyFilterApplied = categories.every((category) => {
    if (category.value === "dateRange") {
      return (
        !filters[category.value]?.fromDate && !filters[category.value]?.toDate
      );
    } else {
      return filters[category.value]?.length === 0;
    }
  });

  // Check if the "Apply" button should be enabled for date range
  const isDateApplyEnabled = (category) => {
    if (category.value === "dateRange") {
      return (
        filters[category.value]?.fromDate || filters[category.value]?.toDate
      );
    }
    return false;
  };

  const adjustFilters = (updatedFilters, categories) => {
    return Object.keys(updatedFilters).reduce((acc, filterKey) => {
      if (filterKey === "dateRange") {
        acc[filterKey] = updatedFilters[filterKey];
      } else if (updatedFilters[filterKey].includes("all")) {
        const category = categories.find((cat) => cat.value === filterKey);
        acc[filterKey] = category.options
          .filter((option) => option.value !== "all")
          .map((option) => option.value);
      } else {
        acc[filterKey] = updatedFilters[filterKey];
      }
      return acc;
    }, {});
  };

  const handleCheckboxChange = (categoryValue, optionValue, checked) => {
    const updatedFilters = {
      ...filters,
      [categoryValue]: checked
        ? [...filters[categoryValue], optionValue]
        : filters[categoryValue].filter((item) => item !== optionValue),
    };

    const adjustedFilter = adjustFilters(updatedFilters, categories);

    setFilters(updatedFilters);
    onFilterChange(adjustedFilter);
  };

  const handleClearFilters = () => {
    const filters = categories.reduce((acc, category) => {
      if (category.value === "dateRange") {
        acc[category.value] = { fromDate: null, toDate: null };
      } else {
        acc[category.value] = [];
      }
      return acc;
    }, {});
    setFilters(filters);
    onFilterChange(filters);
  };

  const dropdownContent = (
    <div className="bg-white dark:bg-bgsecondary shadow-[0_0_16px_rgba(0,0,0,0.1)] rounded-md py-4 min-w-56 max-h-80 overflow-y-auto no-scrollbar">
      <div className="px-4">
        <div
          onClick={handleClearFilters}
          className={`text-white py-1 cursor-pointer rounded-md w-1/2 text-center sticky top-8 z-10 ${
            !anyFilterApplied
              ? "bg-secondary bg-gradient-to-b from-[#1ab5c0] to-[#1996a6] hover:from-[#1996a6] hover:to-[#1a7684]"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          Clear Filters
        </div>
      </div>
      <Collapse
        expandIconPosition="end"
        expandIcon={({ isActive }) => (
          <div className={`${isActive ? "rotate-90" : "rotate-0"}`}>
            <ChevronRight color={`${theme === "dark" ? "#fff" : "#000"}`} />
          </div>
        )}
        className="bg-white"
        style={{ border: "none", borderRadius: 0 }}
      >
        {categories.map((category) => (
          <Panel
            style={{ border: "none", borderRadius: 0 }}
            header={
              <div className="flex justify-between items-center dark:text-white">
                <span>{category.label}</span>
              </div>
            }
            key={category.id}
            className="border-0 bg-white dark:bg-bgsecondary [&_.ant-collapse-content]:!border-0 [&_.ant-collapse-content-box]:!py-0 [&_.ant-collapse-content-box]:dark:bg-bgsecondary"
          >
            {category.value === "dateRange" ? (
              <ConfigProvider
                theme={{
                  token: {
                    colorBgBase: theme === "dark" ? "#1e293b" : "#ffffff",
                    colorText: theme === "dark" ? "#f3f4f6" : "#000000",
                    colorTextPlaceholder:
                      theme === "dark" ? "#6b7280" : "#a3a3a3",
                    colorBorder: theme === "dark" ? "#374151" : "#d9d9d9",
                    colorPrimary: theme === "dark" ? "#FAB54E" : "#1996a6",
                  },
                  components: {
                    DatePicker: {
                      colorPrimary: theme === "dark" ? "#FAB54E" : "#1996a6",
                      colorText: theme === "dark" ? "#f3f4f6" : "#000000",
                    },
                  },
                }}
              >
                <div className="flex flex-col gap-2">
                  <DatePicker
                    placeholder="From Date"
                    value={
                      filters[category.value]?.fromDate
                        ? moment(filters[category.value].fromDate)
                        : null
                    }
                    onChange={(date) => {
                      setFilters((prev) => ({
                        ...prev,
                        [category.value]: {
                          ...prev[category.value],
                          fromDate: date ? date.format("YYYY-MM-DD") : null,
                        },
                      }));
                    }}
                    style={{ width: "100%" }}
                  />
                  <DatePicker
                    placeholder="To Date"
                    value={
                      filters[category.value]?.toDate
                        ? moment(filters[category.value].toDate)
                        : null
                    }
                    onChange={(date) => {
                      setFilters((prev) => ({
                        ...prev,
                        [category.value]: {
                          ...prev[category.value],
                          toDate: date ? date.format("YYYY-MM-DD") : null,
                        },
                      }));
                    }}
                    style={{ width: "100%" }}
                  />
                  <Button
                    type="primary"
                    className={`text-white py-1 px-4 rounded-md w-full ${
                      isDateApplyEnabled(category)
                        ? "bg-secondary bg-gradient-to-b from-[#1ab5c0] to-[#1996a6] hover:from-[#1996a6] hover:to-[#1a7684]"
                        : "bg-gray-400 cursor-not-allowed"
                    }`}
                    onClick={() => {
                      onFilterChange(filters);
                    }}
                    disabled={!isDateApplyEnabled(category)}
                  >
                    Apply
                  </Button>
                </div>
              </ConfigProvider>
            ) : (
              <>
                <div className="flex justify-between items-center px-1 py-[2px] rounded-md cursor-pointer dark:hover:bg-gray-600 hover:bg-[#f5f5f5]">
                  <label
                    htmlFor={`${category.value}-all`}
                    className="cursor-pointer flex-1 dark:text-gray-300"
                  >
                    All
                  </label>
                  <ConfigProvider
                    theme={{
                      components: {
                        Checkbox: {
                          colorPrimary:
                            theme === "dark" ? "#FAB54E" : "#1996a6",
                          colorPrimaryHover:
                            theme === "dark" ? "#FAB54E" : "#1996a6",
                        },
                      },
                    }}
                  >
                    <Checkbox
                      id={`${category.value}-all`}
                      checked={
                        filters[category.value]?.length ===
                        category.options.length - 1
                      }
                      indeterminate={
                        filters[category.value]?.length > 0 &&
                        filters[category.value]?.length <
                          category.options.length - 1
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          const allValues = category.options
                            .filter((option) => option.value !== "all")
                            .map((option) => option.value);
                          setFilters((prev) => ({
                            ...prev,
                            [category.value]: allValues,
                          }));
                          onFilterChange({
                            ...filters,
                            [category.value]: allValues,
                          });
                        } else {
                          setFilters((prev) => ({
                            ...prev,
                            [category.value]: [],
                          }));
                          onFilterChange({
                            ...filters,
                            [category.value]: [],
                          });
                        }
                      }}
                    />
                  </ConfigProvider>
                </div>
                {category.options
                  .filter((option) => option.value !== "all")
                  .map((option) => (
                    <div
                      className="flex justify-between items-center px-1 py-[2px] rounded-md cursor-pointer dark:hover:bg-gray-600 hover:bg-[#f5f5f5]"
                      key={option.id}
                    >
                      <label
                        htmlFor={`${category.value}-${option.value}`}
                        className="cursor-pointer flex-1 dark:text-gray-300"
                      >
                        {option.label}
                      </label>
                      <ConfigProvider
                        theme={{
                          components: {
                            Checkbox: {
                              colorPrimary:
                                theme === "dark" ? "#FAB54E" : "#1996a6",
                              colorPrimaryHover:
                                theme === "dark" ? "#FAB54E" : "#1996a6",
                            },
                          },
                        }}
                      >
                        <Checkbox
                          id={`${category.value}-${option.value}`}
                          checked={filters[category.value]?.includes(
                            option.value
                          )}
                          onChange={(e) =>
                            handleCheckboxChange(
                              category.value,
                              option.value,
                              e.target.checked
                            )
                          }
                          indeterminate={false}
                        />
                      </ConfigProvider>
                    </div>
                  ))}
              </>
            )}
          </Panel>
        ))}
      </Collapse>
    </div>
  );

  return (
    <div className="relative">
      <Dropdown
        trigger={["click"]}
        onOpenChange={setOpen}
        placement="bottomLeft"
        dropdownRender={() => dropdownContent}
      >
        <button
          className={`border hover:border-secondary dark:text-gray-300 h-10 flex gap-1 items-center justify-center px-4 py-2 rounded-md ${
            open ? "border-secondary" : "dark:border-gray-800"
          }`}
        >
          <span>
            <FilterIcon
              color={
                open ? "#2397a5" : theme === "dark" ? "#d1d5db" : "#91919280"
              }
              height={"28px"}
              width={"30px"}
            />
          </span>
          <span>Filter</span>
        </button>
      </Dropdown>
    </div>
  );
};

export default MultiCategoryFilter;
